/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\components\DetailCouts.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT DÉTAIL DES COÛTS

📋 DESCRIPTION:
Composant React d'analyse financière détaillée des devis avec gestion des remises.
Calcul automatique de marge brute et répartition des coûts par catégorie.

🎯 RESPONSABILITÉS:
- Analyse détaillée des coûts par catégorie (MO, matières, accessoires, ST)
- Calcul automatique de marge brute basé sur remises fournisseurs
- Système de remises flexible (détaillée par poste ou globale)
- Visualisation graphique de la répartition des coûts
- Gestion des économies de calepinage automatisé
- Interface de validation financière pour l'utilisateur

🔗 DÉPENDANCES:
- types.ts: Interfaces Article et calculs de prix
- MatiereService: Données matières avec marges fournisseurs
- ParametreService: Configuration fournitures et tarifs
- FermetureService: Données accessoires avec marges
- React: Hooks useState, useEffect pour gestion d'état

📡 INTERACTIONS:
- Configurateur: Reçoit articles avec calculs détaillés
- Services: Récupération marges fournisseurs pour calcul MB
- Parent: Transmission remises appliquées via callbacks
- Export: Données financières pour génération devis

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Calcul marge brute automatique selon remises fournisseurs configurées
- Support remises détaillées (par poste) et globales
- Intégration économies calepinage dans analyse financière
- Visualisation graphique pour aide à la décision
- Gestion articles libres (matières, sous-traitance, main d'œuvre)
*/
import React, { useState, useEffect } from 'react';
import { Article } from '../types';
import { matiereService } from '../../../../services/MatiereService';
import { parametreService } from '../../../../services/ParametreService';
import { fermetureService } from '../../../../services/FermetureService';

interface DetailCoutsProps {
  articles: Article[];
  economieOptimisation?: number;
  integrerEconomieCalepinage?: boolean;
  onRemiseChange?: (remises: RemiseConfig) => void;
  remisesAppliquees?: RemiseConfig;
  onAppliquerRemises?: (remises: RemiseConfig) => void;
}

interface RemiseConfig {
  mainOeuvre: number; // Pourcentage de remise sur MO
  matieresFournitures: number; // Pourcentage de remise sur matières/fournitures
  globale?: number; // Pourcentage de remise globale sur le total
  typeRemise?: 'detaillee' | 'globale'; // Type de remise appliquée
}

interface CoutDetail {
  categorie: string;
  montant: number;
  montantAvantRemise?: number;
  details: string;
  remiseAppliquee?: number;
}

interface MargeData {
  margeBruteMatieres: number;
  margeBruteFournitures: number;
  margeBruteTotal: number;
}

const DetailCouts: React.FC<DetailCoutsProps> = ({ 
  articles, 
  economieOptimisation = 0,
  integrerEconomieCalepinage = false,
  onRemiseChange,
  remisesAppliquees = { mainOeuvre: 0, matieresFournitures: 0 },
  onAppliquerRemises
}) => {
  const [matieres, setMatieres] = useState<any[]>([]);
  const [fournitures, setFournitures] = useState<any[]>([]);
  const [margeData, setMargeData] = useState<MargeData>({ margeBruteMatieres: 0, margeBruteFournitures: 0, margeBruteTotal: 0 });
  const [remises, setRemises] = useState<RemiseConfig>({ 
    ...remisesAppliquees, 
    typeRemise: remisesAppliquees.typeRemise || 'detaillee',
    globale: remisesAppliquees.globale || 0
  });
  const [showRemiseSection, setShowRemiseSection] = useState(false);

  // Charger les données de marge
  useEffect(() => {
    const chargerDonneesMarge = async () => {
      try {
        const [matieresData, fournituresData] = await Promise.all([
          matiereService.getMatieres(),
          parametreService.getFournitures()
        ]);
        setMatieres(matieresData);
        setFournitures(fournituresData);
      } catch (error) {
        console.error('Erreur lors du chargement des données de marge:', error);
      }
    };

    chargerDonneesMarge();
  }, []);

  // Calculer la marge brute
  useEffect(() => {
    const calculerMargeBrute = () => {
      let margeBruteMatieres = 0;
      let margeBruteFournitures = 0;

      articles.forEach((article, index) => {
        if (article.prixCalcule?.details?.couts) {
          const couts = article.prixCalcule.details.couts;
          
          // Calculer la marge sur les matières en utilisant les données détaillées du calepinage
          if (couts.matieres && couts.matieres > 0) {
            // Vérifier si on a les données détaillées du calepinage
            if (article.prixCalcule?.details?.calepinage?.matieres) {
              // Calculer la marge pour chaque matière selon sa quantité réelle
              article.prixCalcule.details.calepinage.matieres.forEach(matiereCalepinage => {
                const matiere = matieres.find(m => m.id === matiereCalepinage.id);
                
                if (matiere && matiereCalepinage.longueurMetres) {
                  const marge = matiere.marge || 30;
                  const coutMatiere = matiereCalepinage.longueurMetres * matiere.prix;
                  const margeMatiereSpecifique = (coutMatiere * marge) / 100;
                  margeBruteMatieres += margeMatiereSpecifique;
                }
              });
            } else {
              // Fallback : utiliser l'ancienne méthode si pas de données calepinage
              const matieresUtilisees = [
                article.materiaux?.dessus?.matiere,
                article.materiaux?.dessous?.matiere,
                article.materiaux?.cotes?.matiere
              ].filter(Boolean);

              if (matieresUtilisees.length > 0) {
                let margeMatiereMoyenne = 0;
                let matieresAvecMarge = 0;

                matieresUtilisees.forEach(matiereId => {
                  const matiere = matieres.find(m => m.id === matiereId);
                  
                  if (matiere) {
                    const marge = matiere.marge || 30;
                    margeMatiereMoyenne += marge;
                    matieresAvecMarge++;
                  }
                });

                if (matieresAvecMarge > 0) {
                  margeMatiereMoyenne = margeMatiereMoyenne / matieresAvecMarge;
                  const margeMatiereArticle = (couts.matieres * margeMatiereMoyenne) / 100;
                  margeBruteMatieres += margeMatiereArticle;
                }
              }
            }
          }

          // Calculer la marge sur les accessoires/fournitures
          if (couts.accessoires && couts.accessoires > 0) {
            // Utiliser une marge moyenne des fournitures ou 30% par défaut
            const margeAccessoires = fournitures.length > 0 
              ? fournitures.reduce((sum, f) => sum + (f.marge || 30), 0) / fournitures.length
              : 30;
            
            const margeFournitureArticle = (couts.accessoires * margeAccessoires) / 100;
            margeBruteFournitures += margeFournitureArticle;
          }
        } else if ((article as any).estArticleLibre) {
          // Traiter les articles libres pour la marge brute
          const typeArticleLibre = (article as any).typeArticleLibre;
          const montantArticle = article.prixUnitaire * (article.quantite || 1);
          const margeFournisseur = (article as any).margeFournisseur;
          
          if (typeArticleLibre === 'matiere' && margeFournisseur !== undefined) {
            const margeArticleLibre = (montantArticle * margeFournisseur) / 100;
            margeBruteMatieres += margeArticleLibre;
          } else if (typeArticleLibre === 'sous_traitance' && margeFournisseur !== undefined) {
            const margeArticleLibre = (montantArticle * margeFournisseur) / 100;
            margeBruteFournitures += margeArticleLibre;
          }
        }
      });

      const margeBruteTotal = margeBruteMatieres + margeBruteFournitures;
      
      setMargeData({ margeBruteMatieres, margeBruteFournitures, margeBruteTotal });
    };

    if (articles.length > 0) {
      calculerMargeBrute();
    }
  }, [articles, matieres, fournitures]);

  // Gérer les changements de remise
  const handleRemiseChange = (type: 'mainOeuvre' | 'matieresFournitures', value: number) => {
    const nouvellesRemises = { ...remises, [type]: value };
    setRemises(nouvellesRemises);
    if (onRemiseChange) {
      onRemiseChange(nouvellesRemises);
    }
  };

  // Gérer le changement de remise globale
  const handleRemiseGlobaleChange = (value: number) => {
    const nouvellesRemises = { ...remises, globale: value };
    setRemises(nouvellesRemises);
    if (onRemiseChange) {
      onRemiseChange(nouvellesRemises);
    }
  };

  // Gérer le changement de type de remise
  const handleTypeRemiseChange = (type: 'detaillee' | 'globale') => {
    const nouvellesRemises = { 
      ...remises, 
      typeRemise: type,
      // Réinitialiser les valeurs selon le type
      mainOeuvre: type === 'globale' ? 0 : remises.mainOeuvre,
      matieresFournitures: type === 'globale' ? 0 : remises.matieresFournitures,
      globale: type === 'detaillee' ? 0 : remises.globale || 0
    };
    setRemises(nouvellesRemises);
    if (onRemiseChange) {
      onRemiseChange(nouvellesRemises);
    }
  };

  // Calculer les coûts par catégorie avec remises appliquées
  const calculerCouts = (): CoutDetail[] => {
    let coutMainOeuvre = 0;
    let coutMatieres = 0;
    let coutAccessoires = 0;
    let coutSousTraitance = 0;
    let nombrePieces = 0;

    articles.forEach(article => {
      const quantite = article.quantite || 1;
      nombrePieces += quantite;

      // Gérer les articles libres selon leur type
      if ((article as any).estArticleLibre) {
        const typeArticleLibre = (article as any).typeArticleLibre;
        const montant = article.prixUnitaire * quantite;
        
        if (typeArticleLibre === 'matiere') {
          coutMatieres += montant;
        } else if (typeArticleLibre === 'sous_traitance') {
          coutSousTraitance += montant;
        }
        // Si c'est 'main_oeuvre', on ne l'ajoute pas aux coûts détaillés
      } else if (article.prixCalcule?.details?.couts) {
        // Articles configurés normalement
        const couts = article.prixCalcule.details.couts;
        
        // Les coûts dans prixCalcule sont déjà pour la quantité totale de l'article
        // Il ne faut PAS les multiplier par la quantité
        coutMainOeuvre += (couts.main_oeuvre || 0);
        coutMatieres += (couts.matieres || 0);
        coutAccessoires += (couts.accessoires || 0);
      } else {
        // Si pas de détail de coûts, utiliser le prix unitaire total
        // (cas des articles sans calcul détaillé)
        const montantTotal = article.prixUnitaire * quantite;
        // Par défaut, considérer que c'est de la main d'œuvre
        coutMainOeuvre += montantTotal;
      }
    });

    const couts: CoutDetail[] = [
      {
        categorie: 'Main d\'œuvre',
        montant: coutMainOeuvre,
        details: `Temps de confection pour ${articles.length} article${articles.length > 1 ? 's' : ''} (${nombrePieces} pièce${nombrePieces > 1 ? 's' : ''})`
      },
      {
        categorie: 'Matières',
        montant: coutMatieres,
        details: 'Coût des matières premières (tissus, cuirs, etc.)'
      },
      {
        categorie: 'Accessoires',
        montant: coutAccessoires,
        details: 'Fermetures, mousses, renforts et autres accessoires'
      }
    ];

    // Ajouter la sous-traitance si elle existe
    if (coutSousTraitance > 0) {
      couts.push({
        categorie: 'Sous-traitance',
        montant: coutSousTraitance,
        details: 'Prestations externes et services sous-traités'
      });
    }

    // Ajouter l'économie d'optimisation UNIQUEMENT si le flag est activé
    if (Math.abs(economieOptimisation) > 0.01 && integrerEconomieCalepinage) {
      couts.push({
        categorie: 'Économie calepinage',
        montant: -Math.abs(economieOptimisation),
        details: 'Réduction des coûts grâce à l\'optimisation du placement des pièces'
      });
    }

    return couts;
  };

  const couts = calculerCouts();
  const total = couts.reduce((sum, cout) => sum + cout.montant, 0);
  
  // Calculer le total SANS l'économie pour les pourcentages
  const totalSansEconomie = articles.reduce((sum, article) => sum + (article.prixUnitaire * (article.quantite || 1)), 0);
  
  // ✅ CORRECTION: Calculer le total réel selon le flag integrerEconomieCalepinage
  // Si OFF: total = prix articles (économie NON déduite, conservée en marge)
  // Si ON: total = prix articles - économie (économie déduite, avantage client)
  const totalReel = integrerEconomieCalepinage 
    ? totalSansEconomie - Math.abs(economieOptimisation || 0)
    : totalSansEconomie;

  // ✅ CORRECTION: Ajouter l'économie à la marge brute si toggle OFF (conservée pour l'entreprise)
  const margeBruteAvecEconomie = {
    margeBruteMatieres: margeData.margeBruteMatieres,
    margeBruteFournitures: margeData.margeBruteFournitures,
    margeBruteTotal: margeData.margeBruteTotal + 
      (!integrerEconomieCalepinage && economieOptimisation > 0 ? economieOptimisation : 0)
  };

  // Calculer la marge brute finale avec remises ET économie
  const calculerMargeBruteFinale = () => {
    if (remises.typeRemise === 'globale') {
      const facteurRemise = 1 - (remises.globale || 0) / 100;
      return {
        margeBruteMatieres: margeBruteAvecEconomie.margeBruteMatieres * facteurRemise,
        margeBruteFournitures: margeBruteAvecEconomie.margeBruteFournitures * facteurRemise,
        margeBruteTotal: margeBruteAvecEconomie.margeBruteTotal * facteurRemise
      };
    } else {
      const facteurRemiseMatieres = 1 - remises.matieresFournitures / 100;
      return {
        margeBruteMatieres: margeBruteAvecEconomie.margeBruteMatieres * facteurRemiseMatieres,
        margeBruteFournitures: margeBruteAvecEconomie.margeBruteFournitures * facteurRemiseMatieres,
        margeBruteTotal: margeBruteAvecEconomie.margeBruteMatieres * facteurRemiseMatieres + 
                        margeBruteAvecEconomie.margeBruteFournitures * facteurRemiseMatieres
      };
    }
  };

  const margeBruteFinale = calculerMargeBruteFinale();

  // Calculer le total avec remises appliquées
  const calculerTotalAvecRemises = () => {
    if (remises.typeRemise === 'globale') {
      // Remise globale sur le total
      const remiseGlobale = totalReel * (remises.globale || 0) / 100;
      return totalReel - remiseGlobale;
    } else {
      // Remises détaillées
      const remiseMO = (couts.find(c => c.categorie === 'Main d\'œuvre')?.montant || 0) * remises.mainOeuvre / 100;
      const remiseMatFour = ((couts.find(c => c.categorie === 'Matières')?.montant || 0) + 
                            (couts.find(c => c.categorie === 'Accessoires')?.montant || 0) +
                            (couts.find(c => c.categorie === 'Sous-traitance')?.montant || 0)) * remises.matieresFournitures / 100;
      return totalReel - remiseMO - remiseMatFour;
    }
  };

  const totalAvecRemises = calculerTotalAvecRemises();
  const totalRemises = totalReel - totalAvecRemises;

  // Calculer la marge brute avec remises appliquées
  const calculerMargeBruteAvecRemises = () => {
    if (remises.typeRemise === 'globale') {
      // En remise globale, la marge brute est réduite proportionnellement
      const facteurRemise = 1 - (remises.globale || 0) / 100;
      return {
        margeBruteMatieres: margeData.margeBruteMatieres * facteurRemise,
        margeBruteFournitures: margeData.margeBruteFournitures * facteurRemise,
        margeBruteTotal: margeData.margeBruteTotal * facteurRemise
      };
    } else {
      // En remise détaillée, seules les catégories concernées sont impactées
      const facteurRemiseMatieres = 1 - remises.matieresFournitures / 100;
      const margeBruteMatieres = margeData.margeBruteMatieres * facteurRemiseMatieres;
      const margeBruteFournitures = margeData.margeBruteFournitures * facteurRemiseMatieres;
      
      return {
        margeBruteMatieres,
        margeBruteFournitures,
        margeBruteTotal: margeBruteMatieres + margeBruteFournitures
      };
    }
  };

  const margeBruteAvecRemises = calculerMargeBruteAvecRemises();

  // Fonction pour appliquer les remises au devis
  const handleAppliquerRemises = () => {
    if (onAppliquerRemises) {
      const hasRemises = remises.typeRemise === 'globale' 
        ? (remises.globale || 0) > 0
        : remises.mainOeuvre > 0 || remises.matieresFournitures > 0;
      
      if (hasRemises) {
        onAppliquerRemises(remises);
      }
    }
  };

  // Fonction pour supprimer les remises
  const handleSupprimerRemises = () => {
    const remisesVides = { 
      mainOeuvre: 0, 
      matieresFournitures: 0, 
      globale: 0,
      typeRemise: remises.typeRemise || 'detaillee'
    };
    setRemises(remisesVides);
    if (onAppliquerRemises) {
      onAppliquerRemises(remisesVides);
    }
  };

  if (articles.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-lg font-medium text-gray-900 mb-2">Aucun coût à afficher</p>
        <p className="text-sm text-gray-500">
          Ajoutez des articles au devis pour voir le détail des coûts.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Analyse de marge */}
      {(margeData.margeBruteTotal > 0 || matieres.length > 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-sm font-medium text-blue-900">Analyse de marge brute</h4>
            <div className="text-xs text-blue-600">
              Basée sur les remises fournisseurs configurées
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-900">
                {margeBruteFinale.margeBruteMatieres.toFixed(2)} €
              </div>
              <div className="text-xs text-blue-600">
                Marge sur matières
                {(remises.mainOeuvre > 0 || remises.matieresFournitures > 0 || (remises.globale || 0) > 0) && (
                  <span className="block text-yellow-600 font-medium">
                    (avec remises)
                  </span>
                )}
                {!integrerEconomieCalepinage && economieOptimisation > 0 && (
                  <span className="block text-green-600 font-medium">
                    (+ économie calepinage)
                  </span>
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-900">
                {margeBruteFinale.margeBruteFournitures.toFixed(2)} €
              </div>
              <div className="text-xs text-blue-600">
                Marge sur fournitures
                {(remises.mainOeuvre > 0 || remises.matieresFournitures > 0 || (remises.globale || 0) > 0) && (
                  <span className="block text-yellow-600 font-medium">
                    (avec remises)
                  </span>
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-900">
                {(margeBruteFinale.margeBruteTotal + (couts.find(c => c.categorie === 'Main d\'œuvre')?.montant || 0) * (remises.typeRemise === 'globale' ? (1 - (remises.globale || 0) / 100) : (1 - remises.mainOeuvre / 100))).toFixed(2)} €
              </div>
              <div className="text-xs text-blue-600">
                Marge brute totale (MB + MO hors ST)
                {(remises.mainOeuvre > 0 || remises.matieresFournitures > 0 || (remises.globale || 0) > 0) && (
                  <span className="block text-yellow-600 font-medium">
                    (avec remises)
                  </span>
                )}
                {!integrerEconomieCalepinage && economieOptimisation > 0 && (
                  <span className="block text-green-600 font-medium">
                    (+ économie calepinage)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Remises */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-medium text-yellow-900">Gestion des remises</h4>
          <button
            type="button"
            className="text-xs text-yellow-600 hover:text-yellow-800 underline"
            onClick={() => setShowRemiseSection(!showRemiseSection)}
          >
            {showRemiseSection ? 'Masquer' : 'Afficher'} les contrôles
          </button>
        </div>
        
        {showRemiseSection && (
          <div className="space-y-4">
            {/* Bascule Type de remise */}
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="flex bg-yellow-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => handleTypeRemiseChange('detaillee')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    remises.typeRemise === 'detaillee'
                      ? 'bg-yellow-600 text-white'
                      : 'text-yellow-700 hover:bg-yellow-200'
                  }`}
                >
                  Remise détaillée
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeRemiseChange('globale')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    remises.typeRemise === 'globale'
                      ? 'bg-yellow-600 text-white'
                      : 'text-yellow-700 hover:bg-yellow-200'
                  }`}
                >
                  Remise globale
                </button>
              </div>
            </div>

            {/* Champs de saisie selon le type */}
            {remises.typeRemise === 'detaillee' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-yellow-900 mb-1">
                    Remise sur Main d'œuvre (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={remises.mainOeuvre}
                    onChange={(e) => handleRemiseChange('mainOeuvre', Number(e.target.value))}
                    className="w-full px-3 py-1 text-sm border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-yellow-900 mb-1">
                    Remise sur Matières et fournitures (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={remises.matieresFournitures}
                    onChange={(e) => handleRemiseChange('matieresFournitures', Number(e.target.value))}
                    className="w-full px-3 py-1 text-sm border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>
            ) : (
              <div className="max-w-md mx-auto">
                <label className="block text-xs font-medium text-yellow-900 mb-1 text-center">
                  Remise globale sur le total du devis (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={remises.globale || 0}
                  onChange={(e) => handleRemiseGlobaleChange(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-center"
                  placeholder="0"
                />
              </div>
            )}
            
            {/* Affichage de l'impact des remises */}
            {((remises.typeRemise === 'detaillee' && (remises.mainOeuvre > 0 || remises.matieresFournitures > 0)) ||
              (remises.typeRemise === 'globale' && (remises.globale || 0) > 0)) && (
              <div className="bg-yellow-100 p-3 rounded-md">
                <div className="text-xs text-yellow-800 mb-2">Impact des remises :</div>
                
                {remises.typeRemise === 'detaillee' ? (
                  <div className="grid grid-cols-2 gap-4 text-xs mb-3">
                    {remises.mainOeuvre > 0 && (
                      <div>
                        <span className="font-medium">MO :</span> -{((couts.find(c => c.categorie === 'Main d\'œuvre')?.montant || 0) * remises.mainOeuvre / 100).toFixed(2)} €
                      </div>
                    )}
                    {remises.matieresFournitures > 0 && (
                      <div>
                        <span className="font-medium">Mat./Four. :</span> -{(((couts.find(c => c.categorie === 'Matières')?.montant || 0) + (couts.find(c => c.categorie === 'Accessoires')?.montant || 0)) * remises.matieresFournitures / 100).toFixed(2)} €
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs mb-3 text-center">
                    <span className="font-medium">Remise globale :</span> -{(totalReel * (remises.globale || 0) / 100).toFixed(2)} € ({remises.globale}%)
                  </div>
                )}
                
                {/* Nouveau total avec remises */}
                <div className="border-t border-yellow-200 pt-2 mb-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-medium text-yellow-800">Total avec remises :</span>
                    <span className="font-bold text-yellow-900">{totalAvecRemises.toFixed(2)} € HT</span>
                  </div>
                  <div className="text-xs text-yellow-600">
                    Économie totale : {totalRemises.toFixed(2)} €
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAppliquerRemises}
                    disabled={
                      (remises.typeRemise === 'detaillee' && remises.mainOeuvre === 0 && remises.matieresFournitures === 0) ||
                      (remises.typeRemise === 'globale' && (remises.globale || 0) === 0)
                    }
                    className="flex-1 px-3 py-2 text-xs font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md transition-colors"
                  >
                    Appliquer les remises
                  </button>
                  <button
                    type="button"
                    onClick={handleSupprimerRemises}
                    disabled={
                      (remises.typeRemise === 'detaillee' && remises.mainOeuvre === 0 && remises.matieresFournitures === 0) ||
                      (remises.typeRemise === 'globale' && (remises.globale || 0) === 0)
                    }
                    className="px-3 py-2 text-xs font-medium text-yellow-700 bg-yellow-200 hover:bg-yellow-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed rounded-md transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tableau des coûts */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Catégorie
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Montant (€)
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pourcentage
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {couts.map((cout, index) => {
              const isEconomie = cout.montant < 0;
              // Calculer les pourcentages sur le total SANS l'économie
              const pourcentage = isEconomie 
                ? 0 // L'économie n'a pas de pourcentage
                : totalSansEconomie > 0 
                  ? (Math.abs(cout.montant) / totalSansEconomie) * 100 
                  : 0;
              
              return (
                <tr key={index} className={isEconomie ? 'bg-green-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${isEconomie ? 'text-green-700' : 'text-gray-900'}`}>
                      {cout.categorie}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${isEconomie ? 'text-green-600' : 'text-gray-500'}`}>
                      {cout.details}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className={`text-sm font-medium ${isEconomie ? 'text-green-700' : 'text-gray-900'}`}>
                      {isEconomie ? '-' : ''}{Math.abs(cout.montant).toFixed(2)} €
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className={`text-sm ${isEconomie ? 'text-green-600' : 'text-gray-500'}`}>
                      {isEconomie ? '-' : pourcentage.toFixed(1) + '%'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Résumé total */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-lg">
        <div className="flex justify-between items-center">
          <div>
            <span className="text-sm font-medium text-gray-700">
              Total général
            </span>
            <div className="text-xs text-gray-500 mt-1">
              {articles.length} article{articles.length > 1 ? 's' : ''} • 
              {articles.reduce((sum, a) => sum + (a.quantite || 1), 0)} pièce{articles.reduce((sum, a) => sum + (a.quantite || 1), 0) > 1 ? 's' : ''}
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-gray-900">
              {totalReel.toFixed(2)} € HT
            </div>
            {Math.abs(economieOptimisation) > 0.01 && (
              <div className="text-xs text-green-600 mt-1">
                Économie : {Math.abs(economieOptimisation).toFixed(2)} €
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Encart informatif si économie non intégrée */}
      {Math.abs(economieOptimisation) > 0.01 && !integrerEconomieCalepinage && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">
                💰 Économie calepinage conservée en marge
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  L'optimisation du calepinage génère une économie de <span className="font-bold">+{Math.abs(economieOptimisation).toFixed(2)} €</span> qui est conservée en marge pour l'entreprise.
                </p>
                <p className="mt-1 text-xs text-blue-600">
                  Cette économie n'est pas déduite du prix facturé au client et améliore votre rentabilité.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Graphique simple en barres */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Répartition des coûts</h4>
        <div className="space-y-2">
          {couts.filter(c => c.montant > 0).map((cout, index) => {
            const pourcentage = totalSansEconomie > 0 ? (cout.montant / totalSansEconomie) * 100 : 0;
            const couleurs = [
              'bg-blue-500',
              'bg-green-500', 
              'bg-yellow-500',
              'bg-purple-500',
              'bg-orange-500'
            ];
            
            return (
              <div key={index} className="flex items-center">
                <div className="w-24 text-xs text-gray-600 text-right pr-2">
                  {cout.categorie}
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                  <div 
                    className={`${couleurs[index % couleurs.length]} h-4 rounded-full transition-all duration-300`}
                    style={{ width: `${Math.max(pourcentage, 2)}%` }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                    {pourcentage.toFixed(0)}%
                  </div>
                </div>
                <div className="w-20 text-xs text-gray-600 text-right pl-2">
                  {cout.montant.toFixed(0)} €
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DetailCouts;
